import { useCrud } from './useCrud';
import { STORAGE_KEYS } from '../models/constants';
import { validateClient, createClient, searchClients } from '../models/Client';

/**
 * Client-specific CRUD hook with localStorage persistence
 */
export const useClients = (initialData = []) => {
  // Load initial data from localStorage if available
  const loadInitialData = () => {
    if (initialData.length > 0) return initialData;
    
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.CLIENTS);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading clients from localStorage:', error);
      return [];
    }
  };

  const clientCrud = useCrud(loadInitialData());

  // Override create to include validation (don't save to localStorage yet for batch operations)
  const create = async (clientData) => {
    console.log('Creating client:', clientData);
    
    const client = createClient(clientData);
    const validation = validateClient(client);
    
    console.log('Client validation:', validation);
    
    if (!validation.isValid) {
      return { success: false, errors: validation.errors };
    }

    try {
      const newClient = clientCrud.create(client);
      console.log('Client created:', newClient);
      return { success: true, data: newClient };
    } catch (error) {
      console.error('Error creating client:', error);
      return { success: false, errors: ['Error creating client'] };
    }
  };

  // Override update to include validation and save to localStorage
  const update = async (id, updates) => {
    const existingClient = clientCrud.getById(id);
    if (!existingClient) {
      return { success: false, errors: ['Client not found'] };
    }

    const updatedClient = { ...existingClient, ...updates, updatedAt: new Date() };
    const validation = validateClient(updatedClient);
    
    if (!validation.isValid) {
      return { success: false, errors: validation.errors };
    }

    try {
      clientCrud.update(id, updates);
      const updatedItems = clientCrud.items.map(item => 
        item.id === id ? updatedClient : item
      );
      saveToStorage(updatedItems);
      return { success: true, data: updatedClient };
    } catch (error) {
      console.error('Error updating client:', error);
      return { success: false, errors: ['Error updating client'] };
    }
  };

  // Override remove to save to localStorage
  const remove = (id) => {
    try {
      clientCrud.remove(id);
      saveToStorage(clientCrud.items);
      return { success: true };
    } catch (error) {
      console.error('Error removing client:', error);
      return { success: false, errors: ['Error removing client'] };
    }
  };

  // Save to localStorage
  const saveToStorage = (clients) => {
    try {
      localStorage.setItem(STORAGE_KEYS.CLIENTS, JSON.stringify(clients));
    } catch (error) {
      console.error('Error saving clients to localStorage:', error);
    }
  };

  // Search clients by name or debt code
  const searchClientsByNameOrDebtCode = (searchTerm) => {
    return searchClients(clientCrud.items, searchTerm);
  };

  // Get client by debt code
  const getClientByDebtCode = (debtCode) => {
    return clientCrud.items.find(client => client.debtCode === debtCode);
  };

  return {
    ...clientCrud,
    create,
    update,
    remove,
    searchClientsByNameOrDebtCode,
    getClientByDebtCode,
    saveToStorage: () => saveToStorage(clientCrud.items) // Explicit save using current state
  };
};